import type { Experiment, Variant } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  MoreHorizontal,
  CheckCircle2,
  Scale,
  Trash2,
  Pencil,
  ArrowRight,
  Minus,
  Braces,
  Copy,
  ExternalLink,
} from 'lucide-react';
// import { Eye, Edit2 } from 'lucide-react'; // View Variant, Open Editor - not implemented yet
import { canUseServerSide } from 'definitions/server-side-access';
import { useAuth } from 'hooks/useAuth';
import { cn } from 'helpers/cn';
import { resolveWeights } from 'helpers/weight-distribution';
import { isMissingDestination, isOriginalVariantName, redirectUrlProblem } from 'helpers/variant-fields';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useVariants } from '@/state/experiments/useVariants';
import { isPreStart } from 'helpers/experiment-status';
import { EditWeightsModal } from './EditWeightsModal';
import { AddVariantModal } from './AddVariantModal';
import { EditVariantModal } from './EditVariantModal';
import { EditVariantPayloadModal } from './EditVariantPayloadModal';
import { DuplicateVariantModal } from './DuplicateVariantModal';

type VariantsConfigProps = { experiment: Experiment };

const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';

const CHIP_CLASS = 'shrink-0 rounded-pill border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase';

const MENU_ITEM_CLASS = 'text-dim focus:bg-hover focus:text-foreground rounded-md text-meta';

/** Why Edit Weights is off while a server-decided experiment runs (set-weights.ts answers 409). */
const WEIGHTS_FIXED_REASON = 'Weights are fixed while your server assigns visitors to a running experiment.';

const DESTINATION_ROW_CLASS = 'bg-field flex min-h-7 min-w-0 items-center gap-1.5 rounded-lg px-2 py-1 text-meta';

const copyDestination = async (url: string) => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Destination URL copied');
  } catch {
    toast.error('Could not copy the URL. Select it and copy it by hand.');
  }
};

export const VariantsConfig = ({ experiment }: VariantsConfigProps) => {
  const { id: experimentId, controlVariantId } = experiment;
  const { variants, deleteVariant, setControlVariant } = useVariants(experimentId);
  const { user } = useAuth();
  const [showWeightsModal, setShowWeightsModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);
  const [editingFieldsOf, setEditingFieldsOf] = useState<Variant | null>(null);
  const [duplicatingVariant, setDuplicatingVariant] = useState<Variant | null>(null);
  const [deletingVariant, setDeletingVariant] = useState<Variant | null>(null);
  const [showAddVariant, setShowAddVariant] = useState(false);

  if (!variants) return null;

  // Count non-Original variants to determine if delete should be enabled
  const nonOriginalVariants = variants.filter((v) => !isOriginalVariantName(v.name));
  const canDelete = nonOriginalVariants.length > 1;

  const isDraft = isPreStart(experiment.status);
  // A variant's key and payload are what a server SDK returns, so they are server-side and
  // hidden from anyone who may not use it yet (PER-178), menu item and chips alike.
  const showServerFields = experiment.type === 'Server Side' && canUseServerSide(user);
  const isUrlRedirect = experiment.type === 'URL Redirect';
  const isEnded = experiment.status === 'Ended';
  // The SDK buckets by weight from the generated config; changing the split mid-run would move
  // visitors the server already assigned, so the API refuses it.
  const weightsFixed = experiment.decidedBy === 'server' && experiment.status === 'Running';
  const canEditWeights = (isDraft || experiment.status === 'Running') && !weightsFixed;

  // Floating (null) weights resolve to their even share for display; legacy
  // distributions that no longer sum to 100% fall back to the raw stored value.
  const resolved = resolveWeights(variants);
  const weightById = new Map((resolved ?? []).map((entry) => [entry.id, entry.weight] as const));

  const getGridClass = () => {
    if (variants.length === 3) return 'grid grid-cols-1 gap-2 sm:grid-cols-3';
    if (variants.length === 4) return 'grid grid-cols-1 gap-2 sm:grid-cols-2';
    return 'grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3';
  };

  return (
    <>
      <section className="bg-panel border-line rounded-panel border" data-tour="settings-variants">
        <div className="border-line flex items-start justify-between gap-4 border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="micro-label text-foreground">Variants</h2>
            <p className="supporting mt-1">
              Create and manage different variations of your experiment to test against each other.
            </p>
          </div>

          {/* Anchored only when it holds a button: an empty group has no box to point at. */}
          <div
            className="flex shrink-0 gap-2"
            data-tour={canEditWeights || isDraft ? 'settings-variants-actions' : undefined}
          >
            {canEditWeights && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowWeightsModal(true)}
                className={SECONDARY_BUTTON_CLASS}
              >
                <Scale className="size-3.5" strokeWidth={1.5} />
                Edit Weights
              </Button>
            )}
            {weightsFixed && (
              <Button
                variant="outline"
                size="sm"
                disabled
                title={WEIGHTS_FIXED_REASON}
                aria-label={`Edit Weights: ${WEIGHTS_FIXED_REASON}`}
                className={SECONDARY_BUTTON_CLASS}
              >
                <Scale className="size-3.5" strokeWidth={1.5} />
                Edit Weights
              </Button>
            )}
            {isDraft && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddVariant(true)}
                className={SECONDARY_BUTTON_CLASS}
              >
                <Plus className="size-3.5" strokeWidth={1.5} />
                Add New Variant
              </Button>
            )}
          </div>
        </div>

        <div className="p-4">
          {weightsFixed && <p className="supporting mt-0 mb-3">{WEIGHTS_FIXED_REASON}</p>}
          <div className={getGridClass()}>
            {variants.map((variant: any, idx: number) => {
              const isControl = controlVariantId ? variant.id === controlVariantId : variant.name === 'Original';
              const isOriginal = isOriginalVariantName(variant.name);
              const isDeployed = !!variant.deployed;
              const weight = weightById.get(variant.id) ?? variant.weight ?? 0;
              const changesCount =
                (variant.changes?.length ?? 0) + (variant.globalJavascript ? 1 : 0) + (variant.globalCSS ? 1 : 0);
              const storedUrl: string | null = isUrlRedirect ? variant.redirectUrl?.trim() || null : null;
              // Rows saved before destinations were validated can hold anything, so only a
              // usable one becomes a link; the rest are shown as text, flagged to fix.
              const destination = storedUrl && !redirectUrlProblem(storedUrl) ? storedUrl : null;
              const invalidDestination = storedUrl && !destination ? storedUrl : null;
              const lacksDestination = isUrlRedirect && (isMissingDestination(variant) || !!invalidDestination);
              // A redirect variant usually has no page changes at all, and "0 changes"
              // read as an empty variant. The destination row says what it does instead.
              const showsChanges = !isUrlRedirect || changesCount > 0;

              return (
                <div
                  key={variant.id}
                  className={cn(
                    'bg-panel2 rounded-card hover:bg-hover flex flex-col gap-2 border p-3 transition-colors duration-150',
                    lacksDestination ? 'border-warn-line' : 'border-line',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-foreground truncate text-body font-medium" title={variant.name}>
                        {variant.name}
                      </span>
                      {isControl && <span className={cn(CHIP_CLASS, 'border-line bg-field text-dim')}>control</span>}
                      {/* Short on purpose: "no destination" wrapped to two lines beside a long name. */}
                      {lacksDestination && (
                        <span className={cn(CHIP_CLASS, 'border-warn-line bg-warn-bg text-warn whitespace-nowrap')}>
                          {invalidDestination ? 'invalid' : 'not set'}
                        </span>
                      )}
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Options for ${variant.name}`}
                          data-tour={idx === 0 ? 'settings-variant-menu' : undefined}
                          title={`Options for ${variant.name}`}
                          // size-7, not size-6: 24px was under the hit-target floor for
                          // the only route to rename, re-control or delete a variant.
                          className="text-dim hover:text-foreground hover:bg-hover size-7 shrink-0 rounded-lg p-0"
                        >
                          <MoreHorizontal className="size-4" strokeWidth={1.5} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="border-line bg-panel rounded-lg">
                        {/* <DropdownMenuItem disabled>
                          <Eye className="w-4 h-4 mr-2" />
                          View Variant
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Open Editor
                        </DropdownMenuItem> */}
                        <DropdownMenuItem
                          disabled={isOriginal}
                          onClick={() => !isOriginal && setEditingVariant(variant)}
                          className={MENU_ITEM_CLASS}
                        >
                          <Pencil className="mr-2 size-3.5" strokeWidth={1.5} />
                          {/* A disabled Radix item takes no tooltip, so each rule is
                              stated in the label. "Why can't I delete this variant?"
                              had three possible answers and the menu gave none. */}
                          Edit Variant
                          {isOriginal && <span className="text-dim ml-1">(Original cannot be renamed)</span>}
                        </DropdownMenuItem>
                        {destination && (
                          <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
                            <a href={destination} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="mr-2 size-3.5" strokeWidth={1.5} />
                              Open destination
                            </a>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={isControl}
                          onClick={() => !isControl && setControlVariant(variant.id)}
                          className={MENU_ITEM_CLASS}
                        >
                          <CheckCircle2 className="mr-2 size-3.5" strokeWidth={1.5} />
                          Set as Control
                          {isControl && <span className="text-dim ml-1">(already the control)</span>}
                        </DropdownMenuItem>
                        {showServerFields && (
                          <DropdownMenuItem onClick={() => setEditingFieldsOf(variant)} className={MENU_ITEM_CLASS}>
                            <Braces className="mr-2 size-3.5" strokeWidth={1.5} />
                            Edit key and payload
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!isDraft}
                          onClick={() => isDraft && setDuplicatingVariant(variant)}
                          className={MENU_ITEM_CLASS}
                        >
                          <Copy className="mr-2 size-3.5" strokeWidth={1.5} />
                          Duplicate Variant
                          {!isDraft && <span className="text-dim ml-1">(drafts only)</span>}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-line" />
                        <DropdownMenuItem
                          disabled={!isDraft || isControl || !canDelete}
                          className={cn(MENU_ITEM_CLASS, 'text-danger focus:text-danger')}
                          onClick={() => isDraft && !isControl && canDelete && setDeletingVariant(variant)}
                        >
                          <Trash2 className="mr-2 size-3.5" strokeWidth={1.5} />
                          Delete
                          {!isDraft && <span className="text-dim ml-1">(drafts only)</span>}
                          {isDraft && isControl && <span className="text-dim ml-1">(it is the control)</span>}
                          {isDraft && !isControl && !canDelete && (
                            <span className="text-dim ml-1">(an experiment needs two variants)</span>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <span className="micro-label">id {variant.id}</span>

                  {showServerFields && (
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className={cn('truncate font-mono text-micro', variant.key ? 'text-foreground' : 'text-dim')}
                        title={variant.key ?? undefined}
                      >
                        {variant.key ?? 'no key'}
                      </span>
                      {variant.payload != null && (
                        <span className={cn(CHIP_CLASS, 'border-line bg-field text-dim')}>payload</span>
                      )}
                    </div>
                  )}

                  {isUrlRedirect &&
                    (destination ? (
                      <div className={DESTINATION_ROW_CLASS}>
                        <ArrowRight className="text-dim size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        <a
                          href={destination}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={destination}
                          className="text-accent hover:text-foreground min-w-0 flex-1 truncate font-mono text-micro"
                        >
                          {destination.replace(/^https?:\/\//, '')}
                          <span className="sr-only"> (opens in a new tab)</span>
                        </a>
                        <button
                          type="button"
                          onClick={() => copyDestination(destination)}
                          aria-label={`Copy the destination URL of ${variant.name}`}
                          title="Copy URL"
                          className="text-dim hover:text-foreground hover:bg-hover flex size-6 shrink-0 items-center justify-center rounded-md"
                        >
                          <Copy className="size-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    ) : invalidDestination ? (
                      <div className={cn(DESTINATION_ROW_CLASS, 'text-warn')} title={invalidDestination}>
                        <ArrowRight className="size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate font-mono text-micro">{invalidDestination}</span>
                        <span className="shrink-0">Not a valid address</span>
                      </div>
                    ) : lacksDestination && isEnded ? (
                      <div className={cn(DESTINATION_ROW_CLASS, 'text-dim')}>
                        <Minus className="size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        No destination was set
                      </div>
                    ) : lacksDestination ? (
                      <button
                        type="button"
                        onClick={() => setEditingVariant(variant)}
                        className="border-warn-line bg-warn-bg text-warn hover:bg-hover flex min-h-7 items-center gap-1.5 rounded-lg border border-dashed px-2 py-1 text-left text-meta"
                      >
                        <Plus className="size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        Add a destination URL
                      </button>
                    ) : (
                      <div className={cn(DESTINATION_ROW_CLASS, 'text-dim')}>
                        <Minus className="size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        No redirect: stays on the targeted page
                      </div>
                    ))}

                  {/* The split is a proportion, so it reads as a meter: the control series
                      stays neutral, everything measured against it takes the accent. */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="num text-dim">{(weight / 100).toFixed(2)}% split</span>
                      {isDeployed ? (
                        <span className={cn(CHIP_CLASS, 'border-accent-line text-accent')}>deployed</span>
                      ) : !showsChanges ? null : (
                        <span className={cn(CHIP_CLASS, 'border-line text-dim')}>
                          {changesCount} {changesCount === 1 ? 'change' : 'changes'}
                        </span>
                      )}
                    </div>
                    <div className="bg-track h-[3px] w-full overflow-hidden rounded-pill">
                      <div
                        className={cn('h-full rounded-pill', isControl ? 'bg-bar-muted' : 'bg-accent')}
                        style={{ width: `${Math.min(100, Math.max(0, weight / 100))}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <EditWeightsModal
        experiment={experiment}
        variants={variants}
        open={showWeightsModal}
        onClose={() => setShowWeightsModal(false)}
      />

      {editingVariant && (
        <EditVariantModal
          variant={editingVariant}
          experiment={experiment}
          open={!!editingVariant}
          onClose={() => setEditingVariant(null)}
        />
      )}

      {editingFieldsOf && (
        <EditVariantPayloadModal
          variant={editingFieldsOf}
          experiment={experiment}
          open={!!editingFieldsOf}
          onClose={() => setEditingFieldsOf(null)}
        />
      )}

      {duplicatingVariant && (
        <DuplicateVariantModal
          variant={duplicatingVariant}
          experimentId={experimentId}
          open={!!duplicatingVariant}
          onClose={() => setDuplicatingVariant(null)}
        />
      )}

      <AddVariantModal experiment={experiment} open={showAddVariant} onClose={() => setShowAddVariant(false)} />

      <ConfirmDialog
        title={`Delete "${deletingVariant?.name}"?`}
        open={!!deletingVariant}
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={async () => {
          if (deletingVariant) {
            await deleteVariant(deletingVariant.id);
            setDeletingVariant(null);
          }
        }}
        onCancel={() => setDeletingVariant(null)}
      >
        Are you sure you want to delete this variant? This action cannot be undone.
      </ConfirmDialog>
    </>
  );
};
